import { useServiceStore } from "@/stores/serviceStore";
import { useUserStore } from "@/stores/userStore";
import {
  REFRESH_TOKEN,
  MEMBER_ID,
  setServiceUid,
  RouterPath,
  RouterName,
  LayoutMode,
  SERVICE_CODE,
  setSerivceCode,
} from "@/types";
import useGetAccessToken from "@/use/useGetAccessToken";
import useGetServiceInfo from "@/use/useGetServiceInfo";
import useSetColor from "@/use/useSetColor";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

declare module "vue-router" {
  interface RouteMeta {
    requiresAuth: boolean;
    transitionName: string;
    keepAliveList: string[];
    pcLayoutMode: LayoutMode;
    showZoomControl: boolean;
    isZoomResetRequired: boolean;
    onBackHandlerList: (() => void)[];
  }
}

const getDefaultMetaData = () => {
  return {
    requiresAuth: false,
    transitionName: "",
    keepAliveList: [],
    pcLayoutMode: LayoutMode.SIDE,
    showZoomControl: false,
    isZoomResetRequired: true,
    onBackHandlerList: [],
  };
};

const routes: Array<RouteRecordRaw> = [
  {
    path: RouterPath.MainLogout,
    name: RouterName.MainLogout,
    component: () => import("@/views/main/MainLogout.vue"),
    meta: { ...getDefaultMetaData() },
  },

  {
    path: RouterPath.Login,
    name: RouterName.Login,
    component: () => import("@/views/login/Login.vue"),
    meta: { ...getDefaultMetaData() },
  },
  {
    path: RouterPath.FindLoginInfo,
    name: RouterName.FindLoginInfo,
    component: () => import("@/views/login/FindLoginInfo.vue"),
    meta: { ...getDefaultMetaData() },
  },

  {
    path: RouterPath.TypesOfSignUp,
    name: RouterName.TypesOfSignUp,
    component: () => import("@/views/signup/TypesOfSignUp.vue"),
    meta: { ...getDefaultMetaData() },
  },
  {
    path: RouterPath.TermsOfUse,
    name: RouterName.TermsOfUse,
    component: () => import("@/views/signup/TermsOfUse.vue"),
    meta: { ...getDefaultMetaData() },
  },
  {
    path: RouterPath.Auth,
    name: RouterName.Auth,
    component: () => import("@/views/signup/Auth.vue"),
    meta: { ...getDefaultMetaData() },
  },
  {
    path: RouterPath.AuthResult,
    name: RouterName.AuthResult,
    component: () => import("@/views/signup/AuthResult.vue"),
    meta: { ...getDefaultMetaData() },
  },
  {
    path: RouterPath.IdAuthentication,
    name: RouterName.IdAuthentication,
    component: () => import("@/views/signup/IdAuthentication.vue"),
    meta: { ...getDefaultMetaData() },
  },
  {
    path: RouterPath.SignUp,
    name: RouterName.SignUp,
    component: () => import("@/views/signup/SignUp.vue"),
    meta: { ...getDefaultMetaData() },
  },

  {
    path: RouterPath.LineSearch,
    name: RouterName.LineSearch,
    component: () => import("@/views/line/LineSearch.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.LineInfoDetail,
    name: RouterName.LineInfoDetail,
    component: () => import("@/views/line/LineInfoDetail.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.MapOfNearbyBusStops,
    name: RouterName.MapOfNearbyBusStops,
    component: () => import("@/views/map/MapOfNearbyBusStops.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.MapOfLineBusStops,
    name: RouterName.MapOfLineBusStops,
    component: () => import("@/views/map/MapOfLineBusStops.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.MainLogin,
    name: RouterName.MainLogin,
    component: () => import("@/views/main/MainLogin.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },

  {
    path: RouterPath.BookFail,
    name: RouterName.BookFail,
    component: () => import("@/views/book/BookFail.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.BookSuccess,
    name: RouterName.BookSuccess,
    component: () => import("@/views/book/BookSuccess.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.BookConfirmation,
    name: RouterName.BookConfirmation,
    component: () => import("@/views/book/BookConfirmation.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },

  {
    path: RouterPath.BookHistory,
    name: RouterName.BookHistory,
    component: () => import("@/views/book/BookHistory.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.BookBoardHistory,
    name: RouterName.BookBoardHistory,
    component: () => import("@/views/history/BookBoardHistory.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.BoardHistory,
    name: RouterName.BoardHistory,
    component: () => import("@/views/board/BoardHistory.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.BusStopInfoMap,
    name: RouterName.BusStopInfoMap,
    component: () => import("@/views/map/BusStopInfoMap.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.PaymentHistory,
    name: RouterName.PaymentHistory,
    component: () => import("@/views/payment/PaymentHistory.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.ReportHistory,
    name: RouterName.ReportHistory,
    component: () => import("@/views/history/ReportHistory.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.Contact,
    name: RouterName.Contact,
    component: () => import("@/views/mypage/Contact.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.MyPage,
    name: RouterName.MyPage,
    component: () => import("@/views/mypage/MyPage.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.MyInfo,
    name: RouterName.MyInfo,
    component: () => import("@/views/mypage/myinfo/MyInfo.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.MyInfoAuth,
    name: RouterName.MyInfoAuth,
    component: () => import("@/views/mypage/myinfo/MyInfoAuth.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.EditMyInfo,
    name: RouterName.EditMyInfo,
    component: () => import("@/views/mypage/myinfo/EditMyInfo.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.ChangePassword,
    name: RouterName.ChangePassword,
    component: () => import("@/views/mypage/myinfo/ChangePassword.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.ChangeExpiredPassword,
    name: RouterName.ChangeExpiredPassword,
    component: () => import("@/views/mypage/myinfo/ChangeExpiredPassword.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.ChangePhoneNumber,
    name: RouterName.ChangePhoneNumber,
    component: () => import("@/views/mypage/myinfo/ChangePhoneNumber.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.ChangeAddress,
    name: RouterName.ChangeAddress,
    component: () => import("@/views/mypage/myinfo/ChangeAddress.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.WithDrawal,
    name: RouterName.WithDrawal,
    component: () => import("@/views/mypage/myinfo/WithDrawal.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.UsageGuide,
    name: RouterName.UsageGuide,
    component: () => import("@/views/mypage/guides/UsageGuide.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.TutorialGuide,
    name: RouterName.TutorialGuide,
    component: () => import("@/views/mypage/guides/TutorialGuide.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.BusStopLayout,
    name: RouterName.BusStopLayout,
    component: () => import("@/views/mypage/BusStopLayout.vue"),
    meta: {
      ...getDefaultMetaData(),
      requiresAuth: true,
      showZoomControl: true,
    },
  },
  {
    path: RouterPath.Notice,
    name: RouterName.Notice,
    component: () => import("@/views/mypage/Notice.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.MobileId,
    name: RouterName.MobileId,
    component: () => import("@/views/mypage/MobileId.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.InsideShuttleMap,
    name: RouterName.InsideShuttleMap,
    component: () => import("@/views/mypage/InsideShuttleMap.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.Chat,
    name: RouterName.Chat,
    component: () => import("@/views/main/Chat.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.NewRouteApplicationList,
    name: RouterName.NewRouteApplicationList,
    component: () => import("@/views/bbs/newRoute/NewRouteApplicationList.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.NewRouteApplicationDetail,
    name: RouterName.NewRouteApplicationDetail,
    component: () => import("@/views/bbs/newRoute/NewRouteApplication.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.NewRouteApplication,
    name: RouterName.NewRouteApplication,
    component: () => import("@/views/bbs/newRoute/NewRouteApplication.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.RouteDemandSurvey,
    name: RouterName.RouteDemandSurvey,
    component: () => import("@/views/mypage/RouteDemandSurvey.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.BusCompanyList,
    name: RouterName.BusCompanyList,
    component: () => import("@/views/mypage/BusCompanyList.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.VocList,
    name: RouterName.VocList,
    component: () => import("@/views/bbs/voc/VocList.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.VocDetail,
    name: RouterName.VocDetail,
    component: () => import("@/views/bbs/voc/Voc.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.Voc,
    name: RouterName.Voc,
    component: () => import("@/views/bbs/voc/Voc.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.LostFoundList,
    name: RouterName.LostFoundList,
    component: () => import("@/views/bbs/lostFound/LostFoundList.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.LostDetail,
    name: RouterName.LostDetail,
    component: () => import("@/views/bbs/lostFound/LostDetail.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.LostCreate,
    name: RouterName.LostCreate,
    component: () => import("@/views/bbs/lostFound/LostEdit.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.LostEdit,
    name: RouterName.LostEdit,
    component: () => import("@/views/bbs/lostFound/LostEdit.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.FoundDetail,
    name: RouterName.FoundDetail,
    component: () => import("@/views/bbs/lostFound/FoundDetail.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.ServiceEvaluation,
    name: RouterName.ServiceEvaluation,
    component: () => import("@/views/evaluation/ServiceEvaluation.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },
  {
    path: RouterPath.BoardEvaluation,
    name: RouterName.BoardEvaluation,
    component: () => import("@/views/evaluation/BoardEvaluation.vue"),
    meta: { ...getDefaultMetaData(), requiresAuth: true },
  },

  /** 외부 접속용 약관 링크 */
  {
    path: RouterPath.TermsOutLink,
    name: RouterName.TermsOutLink,
    component: () => import("@/views/outlink/TermsOutLink.vue"),
    meta: { ...getDefaultMetaData(), pcLayoutMode: LayoutMode.FULL },
  },

  /** iOS 심사용 페이지 */
  {
    path: RouterPath.ReviewMain,
    name: RouterName.ReviewMain,
    component: () => import("@/views/review/ReviewMain.vue"),
    meta: { ...getDefaultMetaData() },
  },
  {
    path: RouterPath.ReviewSearch,
    name: RouterName.ReviewSearch,
    component: () => import("@/views/review/ReviewSearch.vue"),
    meta: { ...getDefaultMetaData() },
  },
  {
    path: RouterPath.ReviewLineInfoDetail,
    name: RouterName.ReviewLineInfoDetail,
    component: () => import("@/views/review/ReviewLineInfoDetail.vue"),
    meta: { ...getDefaultMetaData() },
  },

  /** 구성원 인증 승인/반려 결과 페이지 */
  {
    path: RouterPath.Cert,
    name: RouterName.Cert,
    component: () => import("@/views/cert/Cert.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), pcLayoutMode: LayoutMode.FULL },
  },
  {
    path: RouterPath.AuthConfirm,
    name: RouterName.AuthConfirm,
    component: () => import("@/views/cert/AuthConfirm.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), pcLayoutMode: LayoutMode.FULL },
  },
  {
    path: RouterPath.AuthReject,
    name: RouterName.AuthReject,
    component: () => import("@/views/cert/AuthReject.vue"),
    props: true,
    meta: { ...getDefaultMetaData(), pcLayoutMode: LayoutMode.FULL },
  },

  {
    path: RouterPath.PageNotFound,
    name: RouterName.PageNotFound,
    component: () => import("@/views/error/PageNotFound.vue"),
    meta: { ...getDefaultMetaData(), pcLayoutMode: LayoutMode.FULL },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: RouterPath.PageNotFound,
  },
];

const router = createRouter({
  history: createWebHistory("/client"), // base url 처리
  routes,
  linkActiveClass: "active",
  linkExactActiveClass: "active",
});

let pageStack: string[] = [];

const updateServiceInfo = async () => {
  const serviceStore = useServiceStore();
  const serviceInfoResult = await serviceStore.getServiceInfo();
  if (!serviceInfoResult.isSuccess) return false;

  setServiceUid(serviceStore.serviceUid);
  if (serviceInfoResult.data?.color) {
    useSetColor(serviceInfoResult.data.color);
  }
  return true;
};

const updateToken = async (memberId: string, refreshToken: string) => {
  const userStore = useUserStore();
  return await userStore.updateToken(memberId, refreshToken);
};

const handleAutoLogin = async () => {
  const accessToken = useGetAccessToken();
  const memberId = localStorage.getItem(MEMBER_ID);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);

  if (accessToken && memberId && refreshToken) {
    const result = await updateServiceInfo();

    if (!result) {
      return { name: RouterName.PageNotFound };
    }

    const updateTokenResult = await updateToken(memberId, refreshToken);

    if (updateTokenResult.isSuccess) {
      const shouldChangePassword = updateTokenResult.data?.isPasswordExpired;

      return shouldChangePassword
        ? { name: RouterName.ChangeExpiredPassword }
        : { name: RouterName.MainLogin };
    }
  }
};

router.beforeEach(async (to, from, next) => {
  const fromName = from.name as string;
  const toName = to.name as string;
  const serviceCode = to.params?.serviceCode?.toString();

  from.meta.onBackHandlerList = [];

  if (!serviceCode && toName !== RouterName.PageNotFound) {
    return next({ name: RouterName.PageNotFound });
  }

  if (serviceCode !== SERVICE_CODE) {
    setSerivceCode(serviceCode);
  }

  // 예약 시도 후(성공, 실패) 처리
  const isBookRedirectPage =
    fromName &&
    (toName === RouterName.BookSuccess || toName === RouterName.BookFail);
  if (isBookRedirectPage) {
    return next();
  }

  // 자동 로그인 처리
  const isEntryPage =
    (!fromName && toName === RouterName.MainLogout) ||
    to.params.isEntryPage === "true";
  if (isEntryPage) {
    const buildForIosReview =
      process.env.VUE_APP_BUILD_FOR_IOS_REVIEW === "true";
    if (buildForIosReview) {
      // iOS 심사용으로 빌드된 경우
      return next({ name: RouterName.ReviewMain, params: { serviceCode } });
    } else {
      // 일반적인 경우
      const autoLoginResult = await handleAutoLogin();
      if (autoLoginResult) {
        return next({ ...autoLoginResult, params: { serviceCode } });
      } else {
        pageStack = [toName];
        to.meta.keepAliveList = pageStack;
        return next();
      }
    }
  }

  const serviceInfo = useGetServiceInfo();
  const entryPage =
    serviceInfo?.isPublicLineApi || serviceInfo?.isPublicTalkApi
      ? RouterName.MainLogout
      : RouterName.Login;

  // 권한이 없는 경우 처리
  const accessToken = useGetAccessToken();
  if (to.meta.requiresAuth && !accessToken) {
    return next({ name: entryPage, params: { serviceCode } });
  }

  // 로그인 상태에서 로그인 전 화면으로 이동하는 경우 페이지 이동 막기
  if (
    accessToken &&
    fromName === RouterName.MainLogin &&
    (toName === RouterName.MainLogout || toName === RouterName.SignUp)
  ) {
    return next(false);
  }

  // 노선 관련 페이지에 권한이 없는 경우 처리
  const isUnauthorizedForLine = !serviceInfo?.isPublicLineApi && !accessToken;
  const linePages: string[] = [
    RouterName.LineSearch,
    RouterName.LineInfoDetail,
    RouterName.MapOfNearbyBusStops,
  ];

  if (isUnauthorizedForLine && linePages.includes(toName)) {
    return next({ name: RouterName.Login, params: { serviceCode } });
  }

  // 뒤로가기 처리
  if (from.meta.willGoBack || pageStack.includes(toName)) {
    pageStack = pageStack.filter((page) => page !== fromName);
    to.meta.keepAliveList = pageStack;
    to.meta.transitionName = "backward";
    return next();
  }

  if (toName === RouterName.BookConfirmation) {
    pageStack = [];
    to.meta.keepAliveList = pageStack;
    to.meta.transitionName = "slide-up";
    return next();
  }

  /** 페이지 스택에 저장되지 않아야 할 페이지들 */
  const noStackPages: string[] = [RouterName.TermsOfUse, RouterName.MyInfoAuth];
  if (noStackPages.includes(toName)) {
    to.meta.keepAliveList = pageStack;
    to.meta.transitionName = "forward";
    return next();
  }

  // From, To
  const resetPageStackPairOfPages = [
    [RouterName.SignUp, RouterName.MainLogin],
    [RouterName.SignUp, RouterName.MainLogout],
    [RouterName.SignUp, RouterName.Login],
    [RouterName.Login, RouterName.MainLogin],
    [RouterName.BookFail, RouterName.MainLogin],
    [RouterName.BookSuccess, RouterName.MainLogin],
    [RouterName.BookConfirmation, RouterName.MainLogin],
    [RouterName.MyPage, RouterName.MainLogout],
    [RouterName.WithDrawal, RouterName.MainLogout],
    [RouterName.MyPage, RouterName.Login],
    [RouterName.WithDrawal, RouterName.Login],
    [RouterName.Cert, RouterName.AuthConfirm],
    [RouterName.Cert, RouterName.AuthReject],
  ];

  const shouldResetPageStack =
    resetPageStackPairOfPages.some(
      (pages) => pages[0] === fromName && pages[1] === toName
    ) || to.params.isInvalidLogin === "true";

  if (shouldResetPageStack) {
    pageStack = [toName];
    to.meta.keepAliveList = pageStack;
    return next();
  }

  pageStack.push(toName);
  to.meta.keepAliveList = pageStack;
  to.meta.transitionName = "forward";

  next();
});

export default router;
